import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useUserContext } from '../contexts/UserContext';
import Navbar from './Navbar';
import Footer from './Footer';
import axios from 'axios';
import { Blocks } from 'react-loader-spinner';
import toast, { Toaster } from 'react-hot-toast';
import { API_URL } from '../config/api';

export default function Traitement() {
  const navigate = useNavigate();
  const { user, clearUser } = useUserContext();

  const [commandes, setCommandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchCommandes = async () => {
      const adminId = user?._id || user?.id;
      if (!adminId) {
        toast.error("adminId manquant, veuillez vous reconnecter");
        return;
      }

      const loadingToast = toast.loading("Chargement des commandes...");

      try {
        const res = await fetch(`${API_URL}/commandes?adminId=${adminId}`);
        const data = await res.json();

        if (!res.ok) throw new Error(data.message || "Erreur serveur");

        if (Array.isArray(data)) {
          const previousCount = parseInt(localStorage.getItem('lastCommandeCount') || '0', 10);
          localStorage.setItem('lastCommandeCount', data.length.toString());

          if (data.length > previousCount && previousCount > 0) {
            toast.success(`🛒 ${data.length - previousCount} nouvelle(s) commande(s) reçue(s)`);
          }

          setCommandes(data);
        } else {
          toast.error("Format des données incorrect pour les commandes");
          setCommandes([]);
        }
      } catch (err) {
        console.error("Erreur chargement commandes admin:", err);
        toast.error("Erreur lors de la récupération des commandes.");
        setCommandes([]);
      } finally {
        toast.dismiss(loadingToast);
        setLoading(false);
      }
    };

    fetchCommandes();
  }, [user]);

  const handleStatusUpdate = async (id, newStatus) => {
    const updateToast = toast.loading("Mise à jour du statut...");
    try {
      await axios.put(`${API_URL}/commandes/${id}/status`, { status: newStatus });
      toast.success("Statut mis à jour !");
      setCommandes((prev) =>
        prev.map((cmd) => (cmd._id === id ? { ...cmd, status: newStatus } : cmd))
      );
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la mise à jour du statut");
    } finally {
      toast.dismiss(updateToast);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'livrée': return '#28a745';
      case 'à livrer': return '#fd7e14';
      case 'en attente': return '#0d6efd';
      case 'annulé': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const filteredCommandes = commandes.filter((c) => {
    const matchSearch = (c?.client?.name || c?.name || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter ? c.status === statusFilter : true;
    const matchDate = dateFilter ? c.createdAt?.startsWith(dateFilter) : true;
    return matchSearch && matchStatus && matchDate;
  });

  const totalPages = Math.ceil(filteredCommandes.length / itemsPerPage);
  const paginatedCommandes = filteredCommandes.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const logoutHandler = () => {
    clearUser();
    navigate('/');
  };

  return (
    <>
      <Navbar logoutHandler={logoutHandler} />
      <div className="dashboard-wrapper">
        <div className="dashboard">
          <div className="right">
            <h1><i className="fa-solid fa-truck me-2"></i>Mes commandes</h1>

            <div className="filter--container--content mb-3">
              <input className='form-control'
                type="text"
                placeholder="Rechercher par client..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">Tous les statuts</option>
                <option value="en attente">En attente</option>
                <option value="à livrer">À livrer</option>
                <option value="livrée">Livré</option>
                <option value="annulé">Annulé</option>
              </select>
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => {
                  setDateFilter(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            {loading ? (
              <div className="d-flex justify-content-center mt-4">
                <Blocks visible={true} height="80" width="80" />
              </div>
            ) : (
              <>
                <table className="table table-bordered table-striped">
                  <thead className="table-dark">
                    <tr>
                      <th>Date</th>
                      <th>Client</th>
                      <th>Adresse</th>
                      <th>Téléphone</th>
                      <th>Total</th>
                      <th>Statut</th>
                      <th>Changer</th>
                      <th>Détails</th>
                      <th>Alerte stock</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCommandes.map((c) => (
                      <tr key={c._id}>
                        <td>{new Date(c.createdAt).toLocaleDateString()}</td>
                        <td>{c.name || c.client?.name || 'Inconnu'}</td>
                        <td>{c.address || c.client?.address || 'N/A'}</td>
                        <td>{c.number || c.client?.number || 'N/A'}</td>
                        <td>{c.totalAmount?.toLocaleString()} FCFA</td>
                        <td>
                          <span style={{
                            backgroundColor: getStatusColor(c.status),
                            color: '#fff',
                            padding: '5px 10px',
                            borderRadius: '20px',
                            fontWeight: 'bold',
                            textTransform: 'capitalize'
                          }}>
                            {c.status}
                          </span>
                        </td>
                        <td>
                          <select
                            value={c.status}
                            onChange={(e) => handleStatusUpdate(c._id, e.target.value)}
                          >
                            <option value="en attente">En attente</option>
                            <option value="à livrer">À livrer</option>
                            <option value="livrée">Livrée</option>
                            <option value="annulé">Annulé</option>
                          </select>
                        </td>
                        <td>
                          <Link to={`/detailCommande/${c._id}`}>
                            <button className="btn btn-sm btn-info">Détails</button>
                          </Link>
                        </td>
                        <td>
                          {c.cart?.some(item => item.stockInsuffisant) ? (
                            <span style={{
                              backgroundColor: '#dc3545',
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '10px',
                              fontSize: '0.9em'
                            }}>
                              Stock insuffisant
                            </span>
                          ) : (
                            <span style={{ color: '#28a745', fontSize: '0.9em' }}>OK</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {paginatedCommandes.length === 0 && (
                      <tr>
                        <td colSpan="9" className="text-center">Aucune commande trouvée</td>
                      </tr>
                    )}
                  </tbody>
                </table>

     <div className="pagination-container d-flex justify-content-center my-4">
  <div className="pagination-wrapper d-flex gap-2 flex-wrap">

    {/* Précédent */}
    <button
      disabled={currentPage === 1}
      onClick={() => setCurrentPage((p) => p - 1)}
      className="btn btn-outline-dark"
    >
      ◀
    </button>

    {/* Pages dynamiques */}
    {[...Array(totalPages)].map((_, i) => {
      const page = i + 1;
      // n'affiche que 2 pages avant/après la page courante + première et dernière
      if (
        page === 1 ||
        page === totalPages ||
        (page >= currentPage - 2 && page <= currentPage + 2)
      ) {
        return (
          <button
            key={page}
            className={currentPage === page ? 'btn btn-dark' : 'btn btn-outline-dark'}
            onClick={() => setCurrentPage(page)}
          >
            {page}
          </button>
        );
      } else if (
        page === currentPage - 3 ||
        page === currentPage + 3
      ) {
        return (
          <span key={page} className="btn disabled">
            …
          </span>
        );
      }
      return null; // autres pages non affichées
    })}

    {/* Suivant */}
    <button
      disabled={currentPage === totalPages}
      onClick={() => setCurrentPage((p) => p + 1)}
      className="btn btn-outline-dark"
    >
      ▶
    </button>
  </div>
</div>
                <p className="text-center">Total : {filteredCommandes.length} commande(s)</p>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
      <Toaster position="top-right" />
         <style>{`
        .pagination-container {
  width: 100%;
  display: flex;
  justify-content: center;
  overflow-x: hidden; /* empêche le débordement horizontal */
}

.pagination-wrapper {
  max-width: 90%;
  flex-wrap: wrap; /* pour que les boutons passent à la ligne si trop longs */
  text-align: center;
}

.pagination-wrapper button {
  min-width: 40px;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.pagination-wrapper button:hover:not(:disabled) {
  transform: scale(1.05);
}
      `}</style>
    </>
  );
}